import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, FileCheck2, FileClock, AlertTriangle, Download } from "lucide-react";

const STATUS = {
    uploaded: ["Uploaded · awaiting review", "bg-amber-100 text-amber-600", FileClock],
    reviewed: ["Reviewed", "bg-teal/15 text-teal", FileCheck2],
    needs_update: ["Needs update", "bg-red-100 text-red-600", AlertTriangle],
};

export function Documents() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState("");
    const load = () => api.get("/me/documents").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;

    const upload = async (key, file) => {
        if (!file) return;
        setBusy(key);
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/me/documents/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded — our team will review it"); await load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
    };
    const open = async (id) => {
        try { const r = await api.get(`/documents/${id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
        catch { toast.error("Could not open file"); }
    };
    const done = data.checklist.filter((c) => data.documents.some((d) => d.item_key === c.key)).length;

    return (
        <div className="space-y-6" data-testid="documents-tab">
            <div className="glass-card rounded-2xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div><h3 className="font-heading text-xl font-bold text-navy">Smarter Documents</h3><p className="mt-1 text-sm text-slate-500">Upload what your program needs. Files are private to you and our team, and every upload is reviewed by a person.</p></div>
                    <div className="font-heading text-2xl font-extrabold text-teal" data-testid="documents-progress">{done}/{data.checklist.length}</div>
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-navy transition-all" style={{ width: `${(done / data.checklist.length) * 100}%` }} /></div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
                {data.checklist.map((c) => <Item key={c.key} c={c} doc={data.documents.find((d) => d.item_key === c.key)} busy={busy === c.key} onUpload={(f) => upload(c.key, f)} onOpen={open} />)}
            </div>
            <p className="text-xs text-slate-500">Accepted: PDF, JPG, PNG, WEBP, DOC/DOCX, TXT · up to 10 MB. Please don't upload full Social Security numbers unless a specific program step requires it.</p>
        </div>
    );
}

function Item({ c, doc, busy, onUpload, onOpen }) {
    const ref = useRef(null);
    const [label, cls, Icon] = STATUS[doc?.status] || ["Not uploaded", "bg-slate-100 text-slate-500", Upload];
    return (
        <div className="glass-card rounded-2xl p-5" data-testid={`doc-item-${c.key}`}>
            <div className="flex items-start justify-between gap-3">
                <div><div className="font-heading font-bold text-navy">{c.label}</div><div className="mt-1 text-xs text-slate-500">{c.desc}</div></div>
                <Badge className={`${cls} shrink-0 hover:bg-inherit`} data-testid={`doc-status-${c.key}`}><Icon className="mr-1 h-3 w-3" /> {label}</Badge>
            </div>
            {doc && <button onClick={() => onOpen(doc.id)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-teal hover:underline" data-testid={`doc-open-${c.key}`}><Download className="h-3.5 w-3.5" /> {doc.original_filename}</button>}
            {doc?.note && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{doc.note}</p>}
            <input ref={ref} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`doc-input-${c.key}`} />
            <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`doc-upload-${c.key}`} className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 text-sm font-semibold text-slate-700 transition-colors hover:border-teal hover:text-teal disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> {doc ? "Replace file" : "Upload file"}</>}
            </button>
        </div>
    );
}
